export interface DailyJournalPromptStep {
  minuteLabel: string;
  instruction: string;
}

export interface DailyJournalPromptLink {
  label: string;
  href: string;
}

export interface DailyJournalPrompt {
  id: string;
  title: string;
  theme: string;
  themeSlug: string;
  prompt: string;
  gentlePrompt: string;
  deeperPrompt: string;
  closePrompt: string;
  whyUse: string;
  sessionIntention: string;
  sessionSteps: DailyJournalPromptStep[];
  relatedLinks: DailyJournalPromptLink[];
}

export interface DailyJournalPromptContext {
  prompt: DailyJournalPrompt;
  isoDate: string;
  displayDate: string;
  weekday: string;
}

const PACIFIC_TIME_ZONE = 'America/Los_Angeles';
const DAY_IN_MS = 24 * 60 * 60 * 1000;

// Anchor program for Prompt of the Day sessions. user_sessions.program_id is a
// NOT NULL FK, so every daily-prompt session points at this single hidden,
// unpublished program row (seeded in the daily_prompts migration). Deleting
// that row cascades away all daily-prompt session history.
export const DAILY_PROMPT_ANCHOR_PROGRAM_ID = '00000000-0000-4000-8000-00000000da11';

export const DAILY_JOURNAL_PROMPTS: DailyJournalPrompt[] = [
  {
    id: 'smallest-honest-start',
    title: 'The smallest honest start',
    theme: 'Beginner',
    themeSlug: 'beginner',
    prompt: 'What is one true sentence about how I am arriving at the page today?',
    gentlePrompt: 'What do I notice first: my body, my mood, my thoughts, or my surroundings?',
    deeperPrompt: 'What have I been carrying quietly that could use a little space on the page?',
    closePrompt: 'What is one kind next step I can take after this session?',
    whyUse:
      'Use this prompt when journaling feels too big or too vague. One honest sentence is enough to begin.',
    sessionIntention:
      'Start with low pressure, then let the page reveal what needs attention.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the first true sentence without polishing it.' },
      { minuteLabel: '2-7 min', instruction: 'Follow the strongest word in that sentence and keep writing by hand.' },
      { minuteLabel: '7-10 min', instruction: 'Underline the line that feels most useful and write one next step.' },
    ],
    relatedLinks: [
      { label: 'Journal prompts for beginners', href: '/journal-prompts/for-beginners' },
      { label: 'Morning journal prompts', href: '/journal-prompts/morning' },
      { label: 'Browse all prompt themes', href: '/journal-prompts' },
    ],
  },
  {
    id: 'morning-direction',
    title: 'Morning direction',
    theme: 'Morning',
    themeSlug: 'morning',
    prompt: 'What would make today feel steady, even if it does not go perfectly?',
    gentlePrompt: 'What is one thing I can make easier for myself today?',
    deeperPrompt: 'Where am I confusing urgency with importance right now?',
    closePrompt: 'What do I want to remember when the day gets noisy?',
    whyUse:
      'Use this prompt at the start of the day when you want direction without pressure.',
    sessionIntention:
      'Name a steady point for the day before tasks, messages, and other people set the tone.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the word steady at the top of the page and list what it could mean today.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt with specific actions, not ideals.' },
      { minuteLabel: '7-10 min', instruction: 'Choose one sentence to carry into the day.' },
    ],
    relatedLinks: [
      { label: 'Morning journal prompts', href: '/journal-prompts/morning' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
      { label: 'Guided journaling app', href: '/guided-journaling-app' },
    ],
  },
  {
    id: 'gratitude-specifics',
    title: 'Specific gratitude',
    theme: 'Gratitude',
    themeSlug: 'gratitude',
    prompt: 'What small moment from the last 24 hours deserves more attention than I gave it?',
    gentlePrompt: 'What ordinary comfort helped me yesterday or today?',
    deeperPrompt: 'What does this moment reveal about what I value?',
    closePrompt: 'How can I make room for one more moment like that this week?',
    whyUse:
      'Use this prompt when gratitude lists start feeling generic. Specific moments carry more emotional truth.',
    sessionIntention:
      'Move from vague thankfulness into concrete memory, detail, and meaning.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List three small moments from the last day.' },
      { minuteLabel: '2-7 min', instruction: 'Pick one and describe it with sensory detail.' },
      { minuteLabel: '7-10 min', instruction: 'Write why it mattered and what it says about your needs.' },
    ],
    relatedLinks: [
      { label: 'Gratitude journal prompts', href: '/journal-prompts/gratitude' },
      { label: 'Morning journal prompts', href: '/journal-prompts/morning' },
      { label: 'Pricing', href: '/pricing' },
    ],
  },
  {
    id: 'decision-clarity',
    title: 'Decision clarity',
    theme: 'Decisions',
    themeSlug: 'decisions',
    prompt: 'What choice would become clearer if I stopped trying to make it perfect?',
    gentlePrompt: 'What are the real options in front of me, without adding extra imaginary ones?',
    deeperPrompt: 'What am I afraid this decision will say about me?',
    closePrompt: 'What is the smallest reversible step I can take next?',
    whyUse:
      'Use this prompt when overthinking turns a decision into a maze.',
    sessionIntention:
      'Separate the actual choice from the pressure to solve your whole life at once.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the decision in one plain sentence.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt and name the fear underneath perfection.' },
      { minuteLabel: '7-10 min', instruction: 'Circle the next step that would give you better information.' },
    ],
    relatedLinks: [
      { label: 'Decision journal prompts', href: '/journal-prompts/decisions' },
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
      { label: 'Browse prompt themes', href: '/journal-prompts' },
    ],
  },
  {
    id: 'nervous-system-check-in',
    title: 'Nervous-system check-in',
    theme: 'Wellness',
    themeSlug: 'wellness',
    prompt: 'What is my body asking for that my schedule has been ignoring?',
    gentlePrompt: 'Where do I feel tension or ease right now?',
    deeperPrompt: 'What pattern keeps making my body send the same signal?',
    closePrompt: 'What support, rest, food, movement, or boundary would help today?',
    whyUse:
      'Use this prompt when your thoughts are loud but your body may have the clearer signal.',
    sessionIntention:
      'Let the page become a check-in, not another task to perform.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Scan from head to feet and write what you notice.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt without judging the answer.' },
      { minuteLabel: '7-10 min', instruction: 'Write one care action that is small enough to do today.' },
    ],
    relatedLinks: [
      { label: 'Wellness journal prompts', href: '/journal-prompts/wellness' },
      { label: 'Self-care journal prompts', href: '/journal-prompts/self-care' },
    ],
  },
  {
    id: 'self-discovery-patterns',
    title: 'Pattern spotting',
    theme: 'Self-Discovery',
    themeSlug: 'self-discovery',
    prompt: 'What pattern in my life keeps asking to be understood instead of repeated?',
    gentlePrompt: 'Where have I noticed the same feeling, choice, or reaction more than once?',
    deeperPrompt: 'What does this pattern protect me from, and what does it cost me?',
    closePrompt: 'What would interrupt the pattern gently this week?',
    whyUse:
      'Use this prompt when you keep circling the same situation and want insight without self-blame.',
    sessionIntention:
      'Notice a recurring pattern with curiosity before trying to change it.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Name the pattern as neutrally as possible.' },
      { minuteLabel: '2-7 min', instruction: 'Write where it shows up and what it might be trying to protect.' },
      { minuteLabel: '7-10 min', instruction: 'Close with one compassionate interruption.' },
    ],
    relatedLinks: [
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
      { label: 'Shadow work prompts', href: '/journal-prompts/shadow-work' },
      { label: 'Guided journaling guide', href: '/guided-journaling' },
    ],
  },
  {
    id: 'evening-release',
    title: 'Evening release',
    theme: 'Evening',
    themeSlug: 'evening',
    prompt: 'What from today can I acknowledge, learn from, and leave on the page tonight?',
    gentlePrompt: 'What part of today deserves a softer ending?',
    deeperPrompt: 'What am I still replaying, and what do I need from that memory?',
    closePrompt: 'What do I want to hand back to tomorrow?',
    whyUse:
      'Use this prompt when the day is over but your mind is still holding tabs open.',
    sessionIntention:
      'Create a closing ritual so the page can hold what your mind keeps rehearsing.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List what is still taking up space from today.' },
      { minuteLabel: '2-7 min', instruction: 'Choose one item and write what it needs: acknowledgment, action, or release.' },
      { minuteLabel: '7-10 min', instruction: 'End with one sentence that begins: For tonight, I can leave...' },
    ],
    relatedLinks: [
      { label: 'Evening journal prompts', href: '/journal-prompts/evening' },
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
    ],
  },
  {
    id: 'confidence-evidence',
    title: 'Evidence of capability',
    theme: 'Confidence',
    themeSlug: 'confidence',
    prompt: 'What evidence do I have that I can handle more than I give myself credit for?',
    gentlePrompt: 'What is one small thing I followed through on recently?',
    deeperPrompt: 'When did I mistake discomfort for proof that I was incapable?',
    closePrompt: 'What proof can I collect today by taking one small action?',
    whyUse:
      'Use this prompt when confidence feels abstract and you need evidence from your own life.',
    sessionIntention:
      'Build self-trust from specific proof instead of forced positivity.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write three small wins or hard moments you stayed with.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt with details.' },
      { minuteLabel: '7-10 min', instruction: 'Choose one piece of proof to keep visible today.' },
    ],
    relatedLinks: [
      { label: 'Confidence journal prompts', href: '/journal-prompts/confidence' },
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Journal prompts for students', href: '/journal-prompts/for-students' },
    ],
  },
  {
    id: 'stress-sorting',
    title: 'Stress sorting',
    theme: 'Stress Relief',
    themeSlug: 'stress-relief',
    prompt: 'What part of my stress is mine to act on, and what part needs to be released or shared?',
    gentlePrompt: 'What is one stressor I can name without solving it right now?',
    deeperPrompt: 'What am I trying to control because uncertainty feels uncomfortable?',
    closePrompt: 'What support or boundary would make this lighter?',
    whyUse:
      'Use this prompt when stress feels like one heavy thing and you need to sort it into pieces.',
    sessionIntention:
      'Name what is actionable, what is emotional, and what may need support.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Make three columns: act on, release, share.' },
      { minuteLabel: '2-7 min', instruction: 'Sort the stressor honestly across the columns.' },
      { minuteLabel: '7-10 min', instruction: 'Pick one tiny action or one person to tell.' },
    ],
    relatedLinks: [
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
      { label: 'Mental health prompts', href: '/journal-prompts-for-mental-health' },
    ],
  },
  {
    id: 'relationship-repair',
    title: 'Relationship repair',
    theme: 'Relationships',
    themeSlug: 'relationships',
    prompt: 'What conversation would become kinder if I wrote down what I actually need first?',
    gentlePrompt: 'What feeling do I want to name before I talk to this person?',
    deeperPrompt: 'What story am I telling myself about their intention, and what else could be true?',
    closePrompt: 'What is one clean sentence I could say without blame?',
    whyUse:
      'Use this prompt before a hard conversation so your needs have words before the moment gets heated.',
    sessionIntention:
      'Clarify feelings, needs, and one grounded sentence before speaking.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write what happened in neutral language.' },
      { minuteLabel: '2-7 min', instruction: 'Name the need beneath your reaction.' },
      { minuteLabel: '7-10 min', instruction: 'Draft one sentence that is honest and kind.' },
    ],
    relatedLinks: [
      { label: 'Relationship journal prompts', href: '/journal-prompts/relationships' },
      { label: 'Journaling prompts for couples', href: '/journal-prompts/for-couples' },
      { label: 'Decision journal prompts', href: '/journal-prompts/decisions' },
    ],
  },
  {
    id: 'creative-opening',
    title: 'Creative opening',
    theme: 'Creativity',
    themeSlug: 'creativity',
    prompt: 'What idea keeps tapping me on the shoulder, even before it is ready to be useful?',
    gentlePrompt: 'What sounds fun to explore with no pressure to finish?',
    deeperPrompt: 'What have I dismissed too quickly because it seemed impractical?',
    closePrompt: 'What is one messy first step I can take before judging the idea?',
    whyUse:
      'Use this prompt when you need permission to catch an idea before it becomes a plan.',
    sessionIntention:
      'Let half-formed ideas survive long enough to become interesting.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List ideas without sorting them.' },
      { minuteLabel: '2-7 min', instruction: 'Pick the idea with the most energy and write around it freely.' },
      { minuteLabel: '7-10 min', instruction: 'Name one playful experiment you could try.' },
    ],
    relatedLinks: [
      { label: 'Creativity journal prompts', href: '/journal-prompts/creativity' },
      { label: 'Deep journal prompts', href: '/journal-prompts/deep' },
      { label: 'Browse prompt themes', href: '/journal-prompts' },
    ],
  },
  {
    id: 'shadow-work-gentle',
    title: 'Gentle shadow work',
    theme: 'Shadow Work',
    themeSlug: 'shadow-work',
    prompt: 'What part of me have I been judging that may actually be trying to protect me?',
    gentlePrompt: 'What reaction have I been embarrassed by lately?',
    deeperPrompt: 'If that reaction had a protective job, what would it be?',
    closePrompt: 'What grounding action can help me close this session gently?',
    whyUse:
      'Use this prompt for careful deeper reflection. If it feels too intense, pause, ground, or choose a gentler prompt.',
    sessionIntention:
      'Approach avoided feelings with curiosity and a clear stopping point.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write a grounding sentence: I can go slowly here.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt without forcing a breakthrough.' },
      { minuteLabel: '7-10 min', instruction: 'Close with what would help your body feel present now.' },
    ],
    relatedLinks: [
      { label: 'Shadow work prompts', href: '/journal-prompts/shadow-work' },
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
    ],
  },
  {
    id: 'goal-behind-the-goal',
    title: 'The goal behind the goal',
    theme: 'Goal Setting',
    themeSlug: 'goal-setting',
    prompt: 'What am I hoping to feel once I reach this goal, and could I invite some of that feeling now?',
    gentlePrompt: 'What goal has been on my mind this week?',
    deeperPrompt: 'If the goal arrived tomorrow, what would actually change in my daily life?',
    closePrompt: 'What is one step that serves both the goal and the feeling behind it?',
    whyUse:
      'Use this prompt when a goal feels heavy or mechanical and you want to reconnect with why it matters.',
    sessionIntention:
      'Trace a goal back to the feeling it promises, then act from there.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the goal at the top of the page in one sentence.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt by hand and name the feeling underneath the goal.' },
      { minuteLabel: '7-10 min', instruction: 'Write one step for this week that honors the feeling, not just the metric.' },
    ],
    relatedLinks: [
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Confidence journal prompts', href: '/journal-prompts/confidence' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
    ],
  },
  {
    id: 'maintenance-list',
    title: 'The maintenance list',
    theme: 'Self-Care',
    themeSlug: 'self-care',
    prompt: 'What actually restores me, as opposed to what just distracts me?',
    gentlePrompt: 'When did I last feel genuinely rested, and what was around me?',
    deeperPrompt: 'What have I been calling self-care that leaves me just as tired?',
    closePrompt: 'What is one restoring thing I can schedule like it matters?',
    whyUse:
      'Use this prompt when your recovery time keeps disappearing into scrolling and errands.',
    sessionIntention:
      'Separate real restoration from convenient distraction, without judgment.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List everything you did to recharge in the last week.' },
      { minuteLabel: '2-7 min', instruction: 'Mark each item: restored me, distracted me, or drained me.' },
      { minuteLabel: '7-10 min', instruction: 'Circle one restorer and write when it happens this week.' },
    ],
    relatedLinks: [
      { label: 'Self-care journal prompts', href: '/journal-prompts/self-care' },
      { label: 'Wellness journal prompts', href: '/journal-prompts/wellness' },
      { label: 'Evening journal prompts', href: '/journal-prompts/evening' },
    ],
  },
  {
    id: 'naming-the-weather',
    title: 'Naming the weather',
    theme: 'Mental Health',
    themeSlug: 'mental-health',
    prompt: 'If my inner state were weather, what is the forecast today, and what does that weather need?',
    gentlePrompt: 'What word comes closest to how I feel right now?',
    deeperPrompt: 'How long has this weather pattern been around, and what usually shifts it?',
    closePrompt: 'What is one way to take care of myself inside this weather instead of fighting it?',
    whyUse:
      'Use this prompt when feelings are hard to name directly. Weather language creates distance without denial.',
    sessionIntention:
      'Describe your inner state honestly using an image that makes it easier to hold.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the forecast: sunny, foggy, stormy, or your own mix.' },
      { minuteLabel: '2-7 min', instruction: 'Describe the weather in detail and when it rolled in.' },
      { minuteLabel: '7-10 min', instruction: 'Write what this weather needs from you today: shelter, patience, or light.' },
    ],
    relatedLinks: [
      { label: 'Mental health prompts', href: '/journal-prompts-for-mental-health' },
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
    ],
  },
  {
    id: 'unsent-letter',
    title: 'The unsent letter',
    theme: 'Deep',
    themeSlug: 'deep',
    prompt: 'What do I need to say to someone that they may never need to hear?',
    gentlePrompt: 'Whose name came to mind first, and what feeling came with it?',
    deeperPrompt: 'What part of this letter is really addressed to me?',
    closePrompt: 'What do I want to do with these words now: keep them, revisit them, or let them rest?',
    whyUse:
      'Use this prompt when a conversation keeps happening in your head. The page can hold it so you do not have to.',
    sessionIntention:
      'Say the whole thing once, on paper, with no audience and no consequences.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the greeting and let the first sentence be blunt.' },
      { minuteLabel: '2-7 min', instruction: 'Keep the pen moving. Do not edit for fairness yet.' },
      { minuteLabel: '7-10 min', instruction: 'Read it back once and write one sentence about what you actually need.' },
    ],
    relatedLinks: [
      { label: 'Deep journal prompts', href: '/journal-prompts/deep' },
      { label: 'Relationship journal prompts', href: '/journal-prompts/relationships' },
      { label: 'Shadow work prompts', href: '/journal-prompts/shadow-work' },
    ],
  },
  {
    id: 'one-thing-morning',
    title: 'The one-thing morning',
    theme: 'Morning',
    themeSlug: 'morning',
    prompt: 'If today could only prove one thing to me, what do I want it to be?',
    gentlePrompt: 'What is the first thing pulling at my attention this morning?',
    deeperPrompt: 'What keeps ending up first that does not deserve first?',
    closePrompt: 'What will I let be enough today?',
    whyUse:
      'Use this prompt on mornings when everything feels equally urgent and nothing feels chosen.',
    sessionIntention:
      'Choose one priority on purpose before the day chooses for you.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Brain-dump everything competing for today onto the page.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt and pick the one thing.' },
      { minuteLabel: '7-10 min', instruction: 'Write the first physical step and when it happens.' },
    ],
    relatedLinks: [
      { label: 'Morning journal prompts', href: '/journal-prompts/morning' },
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
    ],
  },
  {
    id: 'proof-of-today',
    title: 'Proof of today',
    theme: 'Evening',
    themeSlug: 'evening',
    prompt: 'What happened today that deserves to be written down before I forget it?',
    gentlePrompt: 'What is one small moment from today I can still picture?',
    deeperPrompt: 'What does what I remember, and what I forget, say about where my attention goes?',
    closePrompt: 'What from today do I want future me to know happened?',
    whyUse:
      'Use this prompt when days blur together and you want evidence that today actually happened.',
    sessionIntention:
      'Record the day in specifics so it does not dissolve into the week.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List five things that happened today, tiny ones count.' },
      { minuteLabel: '2-7 min', instruction: 'Pick the one with the most feeling and describe it fully.' },
      { minuteLabel: '7-10 min', instruction: 'End with one line addressed to future you.' },
    ],
    relatedLinks: [
      { label: 'Evening journal prompts', href: '/journal-prompts/evening' },
      { label: 'Gratitude journal prompts', href: '/journal-prompts/gratitude' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
    ],
  },
  {
    id: 'thanks-to-a-past-me',
    title: 'Thanks to a past me',
    theme: 'Gratitude',
    themeSlug: 'gratitude',
    prompt: 'What did a past version of me do that present me gets to benefit from?',
    gentlePrompt: 'What is one thing in my life today that took effort to build?',
    deeperPrompt: 'What hard choice did I once make that quietly keeps paying off?',
    closePrompt: 'What can I do today that a future me will want to thank me for?',
    whyUse:
      'Use this prompt when gratitude toward your own effort is overdue. Self-appreciation is a skill.',
    sessionIntention:
      'Notice your own past effort the way you would notice a friend showing up for you.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List three things past you set up: habits, choices, repairs.' },
      { minuteLabel: '2-7 min', instruction: 'Write a short thank-you note to the version of you who did one of them.' },
      { minuteLabel: '7-10 min', instruction: 'Choose one gift to send forward and write what it is.' },
    ],
    relatedLinks: [
      { label: 'Gratitude journal prompts', href: '/journal-prompts/gratitude' },
      { label: 'Confidence journal prompts', href: '/journal-prompts/confidence' },
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
    ],
  },
  {
    id: 'energy-ledger',
    title: 'The energy ledger',
    theme: 'Self-Discovery',
    themeSlug: 'self-discovery',
    prompt: 'What has been feeding my energy lately, and what has been quietly billing it?',
    gentlePrompt: 'When did I feel most awake this week, and when did I feel most drained?',
    deeperPrompt: 'Which drain am I tolerating because admitting it would require a change?',
    closePrompt: 'What is one small transfer I can make from a drain to a feeder this week?',
    whyUse:
      'Use this prompt when tiredness feels constant but unexplained. Energy leaves receipts.',
    sessionIntention:
      'Audit a normal week for what gives and takes energy, in plain columns.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Draw two columns: feeds me, drains me.' },
      { minuteLabel: '2-7 min', instruction: 'Fill both columns from the last seven days, people and tasks included.' },
      { minuteLabel: '7-10 min', instruction: 'Star the biggest item in each column and write one adjustment.' },
    ],
    relatedLinks: [
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
      { label: 'Wellness journal prompts', href: '/journal-prompts/wellness' },
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
    ],
  },
  {
    id: 'borrowed-belief',
    title: 'Borrowed belief',
    theme: 'Confidence',
    themeSlug: 'confidence',
    prompt: 'Who believes in me, and what do they see that I keep discounting?',
    gentlePrompt: 'Whose encouragement do I trust, even a little?',
    deeperPrompt: 'Why do I trust my inner critic more easily than I trust their evidence?',
    closePrompt: 'What would I attempt this week if I borrowed their belief for one day?',
    whyUse:
      'Use this prompt when self-belief runs low. Borrowing an outside view is a legitimate bridge.',
    sessionIntention:
      'Let an accurate outside view of you count as evidence for a day.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the name of someone who believes in you and one thing they have said.' },
      { minuteLabel: '2-7 min', instruction: 'Write what they see, in their voice, as generously as they would say it.' },
      { minuteLabel: '7-10 min', instruction: 'Name one action you would take if their view were simply true.' },
    ],
    relatedLinks: [
      { label: 'Confidence journal prompts', href: '/journal-prompts/confidence' },
      { label: 'Relationship journal prompts', href: '/journal-prompts/relationships' },
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
    ],
  },
  {
    id: 'worry-appointment',
    title: 'The worry appointment',
    theme: 'Stress Relief',
    themeSlug: 'stress-relief',
    prompt: 'If my worries had ten full minutes of my undivided attention, what would they actually say?',
    gentlePrompt: 'What worry has been circling loudest today?',
    deeperPrompt: 'Which of these worries is a real problem, and which is just a rehearsal of one?',
    closePrompt: 'What does the biggest worry need: an action, more information, or just acknowledgment?',
    whyUse:
      'Use this prompt when worry keeps interrupting everything else. Giving it a scheduled slot shrinks it.',
    sessionIntention:
      'Give worry a contained appointment on paper so it stops booking your whole day.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List every worry with no filtering. Small ones count.' },
      { minuteLabel: '2-7 min', instruction: 'Let the loudest one talk on paper until it runs out of new material.' },
      { minuteLabel: '7-10 min', instruction: 'Sort the list: act, ask, or acknowledge. Pick one act item.' },
    ],
    relatedLinks: [
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
      { label: 'Mental health prompts', href: '/journal-prompts-for-mental-health' },
      { label: 'Evening journal prompts', href: '/journal-prompts/evening' },
    ],
  },
  {
    id: 'appreciation-gap',
    title: 'The appreciation gap',
    theme: 'Relationships',
    themeSlug: 'relationships',
    prompt: 'Who am I appreciating silently that deserves to hear it out loud?',
    gentlePrompt: 'Who made something easier for me recently?',
    deeperPrompt: 'What keeps me from saying appreciation out loud: habit, awkwardness, or assuming they know?',
    closePrompt: 'What is one sentence of appreciation I could actually deliver this week?',
    whyUse:
      'Use this prompt when your gratitude for people stays trapped in your head where it helps no one.',
    sessionIntention:
      'Move one piece of appreciation from private thought to deliverable words.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List three people you have appreciated silently this month.' },
      { minuteLabel: '2-7 min', instruction: 'Pick one and write what they did, what it cost them, and what it gave you.' },
      { minuteLabel: '7-10 min', instruction: 'Draft the one sentence you could send or say, and decide when.' },
    ],
    relatedLinks: [
      { label: 'Relationship journal prompts', href: '/journal-prompts/relationships' },
      { label: 'Gratitude journal prompts', href: '/journal-prompts/gratitude' },
      { label: 'Journaling prompts for couples', href: '/journal-prompts/for-couples' },
    ],
  },
  {
    id: 'curiosity-list',
    title: 'The curiosity list',
    theme: 'Creativity',
    themeSlug: 'creativity',
    prompt: 'What have I been curious about lately that I keep postponing because it is not productive?',
    gentlePrompt: 'What did I last look up, read, or watch purely because I wanted to?',
    deeperPrompt: 'What did younger me love that adult me filed away as impractical?',
    closePrompt: 'What is one curiosity I can give twenty unproductive minutes this week?',
    whyUse:
      'Use this prompt when life has become all output and no wonder. Curiosity is fuel, not a detour.',
    sessionIntention:
      'Take inventory of your curiosity before productivity spends the whole budget.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List everything that has sparked your curiosity this month.' },
      { minuteLabel: '2-7 min', instruction: 'Pick the item with the most pull and write why it keeps calling.' },
      { minuteLabel: '7-10 min', instruction: 'Schedule twenty minutes for it and write down when.' },
    ],
    relatedLinks: [
      { label: 'Creativity journal prompts', href: '/journal-prompts/creativity' },
      { label: 'Deep journal prompts', href: '/journal-prompts/deep' },
      { label: 'Browse all prompt themes', href: '/journal-prompts' },
    ],
  },
  {
    id: 'eighty-percent-yes',
    title: 'The 80 percent yes',
    theme: 'Decisions',
    themeSlug: 'decisions',
    prompt: 'What decision am I delaying while I wait for a certainty that is not coming?',
    gentlePrompt: 'What choice keeps reappearing on my mental to-do list?',
    deeperPrompt: 'What would I decide right now if 80 percent sure counted as sure enough?',
    closePrompt: 'What is the cost of waiting another month that I have not been counting?',
    whyUse:
      'Use this prompt when research has quietly become procrastination.',
    sessionIntention:
      'Name the decision, price the delay, and act at 80 percent certainty.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write the delayed decision in one plain sentence.' },
      { minuteLabel: '2-7 min', instruction: 'List what you already know, then what waiting has cost so far.' },
      { minuteLabel: '7-10 min', instruction: 'Write your 80 percent answer and the first step it implies.' },
    ],
    relatedLinks: [
      { label: 'Decision journal prompts', href: '/journal-prompts/decisions' },
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Confidence journal prompts', href: '/journal-prompts/confidence' },
    ],
  },
  {
    id: 'rest-without-earning',
    title: 'Rest without earning it',
    theme: 'Wellness',
    themeSlug: 'wellness',
    prompt: 'What would rest look like today if I did not have to earn it first?',
    gentlePrompt: 'How tired am I, honestly, on a scale from fresh to running on fumes?',
    deeperPrompt: 'Where did I learn that rest has to be deserved?',
    closePrompt: 'What is one piece of unconditional rest I can take in the next 24 hours?',
    whyUse:
      'Use this prompt when you keep postponing rest until the list is done, and the list is never done.',
    sessionIntention:
      'Practice treating rest as maintenance rather than a reward.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write your honest tiredness level and where you feel it.' },
      { minuteLabel: '2-7 min', instruction: 'Answer the main prompt and notice any guilt that shows up. Write to it.' },
      { minuteLabel: '7-10 min', instruction: 'Plan one small rest with a time attached, no conditions.' },
    ],
    relatedLinks: [
      { label: 'Wellness journal prompts', href: '/journal-prompts/wellness' },
      { label: 'Self-care journal prompts', href: '/journal-prompts/self-care' },
      { label: 'Mental health prompts', href: '/journal-prompts-for-mental-health' },
    ],
  },
  {
    id: 'ten-honest-minutes',
    title: 'Ten honest minutes',
    theme: 'Beginner',
    themeSlug: 'beginner',
    prompt: 'What would I write if the only rule were that every sentence has to be true?',
    gentlePrompt: 'What is one plainly true thing about my day so far?',
    deeperPrompt: 'Which true sentence was hardest to write, and why that one?',
    closePrompt: 'What did ten minutes of honesty make room for?',
    whyUse:
      'Use this prompt when you do not know what to write about. Truth is a topic all by itself.',
    sessionIntention:
      'Practice honesty as a writing method: no themes, no goals, just true sentences.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Write three true sentences about right now: room, body, mood.' },
      { minuteLabel: '2-7 min', instruction: 'Keep going. When stuck, start the next sentence with the words: The truth is.' },
      { minuteLabel: '7-10 min', instruction: 'Underline the truest sentence on the page and sit with it for a moment.' },
    ],
    relatedLinks: [
      { label: 'Journal prompts for beginners', href: '/journal-prompts/for-beginners' },
      { label: 'Daily journal prompts', href: '/daily-journal-prompts' },
      { label: 'Guided journaling app', href: '/guided-journaling-app' },
    ],
  },
  {
    id: 'envy-compass',
    title: 'The envy compass',
    theme: 'Shadow Work',
    themeSlug: 'shadow-work',
    prompt: 'What am I envious of lately, and what direction is that envy actually pointing me toward?',
    gentlePrompt: 'Whose life, work, or ease made me feel a twinge recently?',
    deeperPrompt: 'What does this envy claim I am not allowed to want for myself?',
    closePrompt: 'What is one small move toward the thing the envy is pointing at?',
    whyUse:
      'Use this prompt to turn envy from a shameful feeling into useful information about your wants.',
    sessionIntention:
      'Read envy as a compass needle instead of a character flaw.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Name the envy honestly: who, what, and when it flared.' },
      { minuteLabel: '2-7 min', instruction: 'Write what the envy is pointing at, separate from the person it landed on.' },
      { minuteLabel: '7-10 min', instruction: 'Write one permission sentence: I am allowed to want... and finish it.' },
    ],
    relatedLinks: [
      { label: 'Shadow work prompts', href: '/journal-prompts/shadow-work' },
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
    ],
  },
  {
    id: 'quit-list',
    title: 'The quit list',
    theme: 'Goal Setting',
    themeSlug: 'goal-setting',
    prompt: 'What would I need to stop doing for my most important goal to actually have room?',
    gentlePrompt: 'What commitment or habit have I outgrown but kept out of momentum?',
    deeperPrompt: 'What am I afraid quitting would say about me?',
    closePrompt: 'What is one graceful exit I can start this week?',
    whyUse:
      'Use this prompt when your plate is full and your goals are starving. Subtraction is progress too.',
    sessionIntention:
      'Build a quit list with the same care most people give their to-do list.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'List everything currently claiming your time and energy.' },
      { minuteLabel: '2-7 min', instruction: 'Mark what earns its place and what is there by inertia.' },
      { minuteLabel: '7-10 min', instruction: 'Choose one inertia item and write the first step of a graceful exit.' },
    ],
    relatedLinks: [
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Decision journal prompts', href: '/journal-prompts/decisions' },
      { label: 'Stress relief prompts', href: '/journal-prompts/stress-relief' },
    ],
  },
  {
    id: 'future-self-postcard',
    title: 'Postcard from next year',
    theme: 'Self-Discovery',
    themeSlug: 'self-discovery',
    prompt: 'What does my life look like one year from today if the next twelve months go gently right?',
    gentlePrompt: 'What is one thing I hope is different a year from now?',
    deeperPrompt: 'What in that picture depends on choices only I can make?',
    closePrompt: 'What can I do this month that next-year me is clearly standing on?',
    whyUse:
      'Use this prompt when the future feels vague. A specific picture beats a vague hope.',
    sessionIntention:
      'Write from a year ahead with warmth and detail, then work backward to now.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Date the page one year from today.' },
      { minuteLabel: '2-7 min', instruction: 'Write the postcard in present tense: where you are, what a normal day holds.' },
      { minuteLabel: '7-10 min', instruction: 'Underline what required action and write what this month contributes.' },
    ],
    relatedLinks: [
      { label: 'Self-discovery prompts', href: '/journal-prompts/self-discovery' },
      { label: 'Goal-setting prompts', href: '/journal-prompts/goal-setting' },
      { label: 'Deep journal prompts', href: '/journal-prompts/deep' },
    ],
  },
  {
    id: 'kind-re-read',
    title: 'The kind re-read',
    theme: 'Mental Health',
    themeSlug: 'mental-health',
    prompt: 'If I re-read my recent pages the way a kind friend would, what would that friend want me to notice?',
    gentlePrompt: 'What tone do I usually take with myself when I look back at my own words?',
    deeperPrompt: 'What pattern in my pages would a friend point out gently that I keep judging harshly?',
    closePrompt: 'What is one kind, true sentence I can leave for the next time I read back?',
    whyUse:
      'Use this prompt with a few pages already behind you. Your journal holds evidence your inner critic skips.',
    sessionIntention:
      'Practice reading your own writing with the generosity you would give a friend.',
    sessionSteps: [
      { minuteLabel: '0-2 min', instruction: 'Flip back through your last few entries without editing anything.' },
      { minuteLabel: '2-7 min', instruction: 'Write what a kind friend would notice: effort, honesty, quiet progress.' },
      { minuteLabel: '7-10 min', instruction: 'Leave a short kind note in the margin for future you to find.' },
    ],
    relatedLinks: [
      { label: 'Mental health prompts', href: '/journal-prompts-for-mental-health' },
      { label: 'Self-care journal prompts', href: '/journal-prompts/self-care' },
    ],
  },
];

export function getPacificDateParts(date: Date): {
  year: number;
  month: number;
  day: number;
  isoDate: string;
  displayDate: string;
  weekday: string;
} {
  const numericParts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(date);
  const displayParts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIME_ZONE,
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).formatToParts(date);

  const numeric = Object.fromEntries(numericParts.map((part) => [part.type, part.value]));
  const display = Object.fromEntries(displayParts.map((part) => [part.type, part.value]));
  const year = Number(numeric.year);
  const month = Number(numeric.month);
  const day = Number(numeric.day);

  return {
    year,
    month,
    day,
    isoDate: `${numeric.year}-${numeric.month}-${numeric.day}`,
    displayDate: `${display.month} ${display.day}, ${display.year}`,
    weekday: display.weekday || '',
  };
}

export function getPacificIsoDate(date = new Date()): string {
  return getPacificDateParts(date).isoDate;
}

function getRotationPrompt(year: number, month: number, day: number): DailyJournalPrompt {
  const startOfYear = Date.UTC(year, 0, 1);
  const currentDay = Date.UTC(year, month - 1, day);
  const dayOfYear = Math.max(0, Math.floor((currentDay - startOfYear) / DAY_IN_MS));

  return DAILY_JOURNAL_PROMPTS[dayOfYear % DAILY_JOURNAL_PROMPTS.length];
}

export function getDailyJournalPromptForDate(date = new Date()): DailyJournalPrompt {
  const { year, month, day } = getPacificDateParts(date);
  return getRotationPrompt(year, month, day);
}

const ISO_DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Context for a calendar date already expressed as a Pacific ISO date
 * (YYYY-MM-DD), e.g. from a ?d= share link or a daily_prompts row key.
 * Returns null when the string is not a valid calendar date.
 */
export function getDailyJournalPromptContextForIsoDate(
  isoDate: string
): DailyJournalPromptContext | null {
  const match = ISO_DATE_PATTERN.exec(isoDate);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const utcDate = new Date(Date.UTC(year, month - 1, day));
  if (
    utcDate.getUTCFullYear() !== year ||
    utcDate.getUTCMonth() !== month - 1 ||
    utcDate.getUTCDate() !== day
  ) {
    return null;
  }

  const displayParts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }).formatToParts(utcDate);
  const display = Object.fromEntries(displayParts.map((part) => [part.type, part.value]));

  return {
    prompt: getRotationPrompt(year, month, day),
    isoDate,
    displayDate: `${display.month} ${display.day}, ${display.year}`,
    weekday: display.weekday || '',
  };
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

/**
 * Validates a daily_prompts.content jsonb payload into a DailyJournalPrompt.
 * Returns null on any shape mismatch so callers fall back to the rotation.
 */
export function parseDailyPromptContent(value: unknown): DailyJournalPrompt | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Record<string, unknown>;

  const requiredStrings = [
    'id',
    'title',
    'theme',
    'themeSlug',
    'prompt',
    'gentlePrompt',
    'deeperPrompt',
    'closePrompt',
    'whyUse',
    'sessionIntention',
  ] as const;
  for (const key of requiredStrings) {
    if (!isNonEmptyString(record[key])) return null;
  }

  const sessionSteps = record.sessionSteps;
  if (!Array.isArray(sessionSteps) || sessionSteps.length === 0) return null;
  for (const step of sessionSteps) {
    if (!step || typeof step !== 'object') return null;
    const candidate = step as Record<string, unknown>;
    if (!isNonEmptyString(candidate.minuteLabel) || !isNonEmptyString(candidate.instruction)) {
      return null;
    }
  }

  const relatedLinksRaw = Array.isArray(record.relatedLinks) ? record.relatedLinks : [];
  const relatedLinks: DailyJournalPromptLink[] = [];
  for (const link of relatedLinksRaw) {
    if (!link || typeof link !== 'object') continue;
    const candidate = link as Record<string, unknown>;
    if (isNonEmptyString(candidate.label) && isNonEmptyString(candidate.href)) {
      relatedLinks.push({ label: candidate.label, href: candidate.href });
    }
  }

  return {
    id: record.id as string,
    title: record.title as string,
    theme: record.theme as string,
    themeSlug: record.themeSlug as string,
    prompt: record.prompt as string,
    gentlePrompt: record.gentlePrompt as string,
    deeperPrompt: record.deeperPrompt as string,
    closePrompt: record.closePrompt as string,
    whyUse: record.whyUse as string,
    sessionIntention: record.sessionIntention as string,
    sessionSteps: sessionSteps.map((step) => {
      const candidate = step as Record<string, unknown>;
      return {
        minuteLabel: candidate.minuteLabel as string,
        instruction: candidate.instruction as string,
      };
    }),
    relatedLinks,
  };
}

export function getDailyJournalPromptContext(date = new Date()): DailyJournalPromptContext {
  const dateParts = getPacificDateParts(date);

  return {
    prompt: getDailyJournalPromptForDate(date),
    isoDate: dateParts.isoDate,
    displayDate: dateParts.displayDate,
    weekday: dateParts.weekday,
  };
}

export function getDailyJournalPromptStarterPath(limit = 8): DailyJournalPrompt[] {
  return DAILY_JOURNAL_PROMPTS.slice(0, limit);
}

const DAILY_PROMPT_QUERY_MAX_AGE_DAYS = 30;

/** Same window the landing page uses for ?d=: today through 30 days ago. */
export function getSafeDailyPromptIsoDate(rawDate: string | null, todayIso: string): string | undefined {
  const trimmedDate = rawDate?.trim();
  if (!trimmedDate) return undefined;

  const context = getDailyJournalPromptContextForIsoDate(trimmedDate);
  if (!context) return undefined;

  const ageInDays = Math.floor(
    (Date.parse(`${todayIso}T00:00:00.000Z`) - Date.parse(`${context.isoDate}T00:00:00.000Z`)) / DAY_IN_MS
  );
  if (ageInDays < 0 || ageInDays > DAILY_PROMPT_QUERY_MAX_AGE_DAYS) return undefined;
  return context.isoDate;
}

export function dailyPromptPageTitle(displayDate: string, _viewingPast = false): string {
  return `Journal prompt of the day: ${displayDate}`;
}

export function dailyPromptSessionCopy(
  viewingPast: boolean,
  displayDate: string,
  promptText: string
): {
    headingTop: string;
    ariaLabel: string;
    shareTitle: string;
    shareText: string;
    shareLabel: string;
    subtitle: string;
    firstNote: string;
  } {
  const firstNote =
    'Here is what a session looks like. We give you the prompt below and keep the time. Grab your journal and a pen, then press begin.';
  if (viewingPast) {
    return {
      headingTop: displayDate,
      ariaLabel: `Session for ${displayDate}`,
      shareTitle: dailyPromptPageTitle(displayDate, true),
      shareText: `Journal prompt for ${displayDate}: "${promptText}"`,
      shareLabel: `Share the prompt for ${displayDate}`,
      subtitle: `Prompt for ${displayDate}`,
      firstNote,
    };
  }
  return {
    headingTop: 'Today\u2019s',
    ariaLabel: "Today's session",
    shareTitle: 'Journal Prompt of the Day',
    shareText: `Today's journal prompt: "${promptText}"`,
    shareLabel: "Share today's prompt",
    subtitle: "Today's prompt",
    firstNote,
  };
}
